import { useQuery } from "@apollo/client";
import { LoadingSpinner } from "@govlaunch/web";
import { useRouter } from "next/router";
import React from "react";
import { findLanguageByLocale } from "~/intl/locales";
import VendorQuery from "~/lib/queries/VendorQuery";
import { IVendorQuery, IVendorQueryVariables } from "~/lib/queries/__generated__/VendorQuery.generated";
import Redirect from "~/lib/route/Redirect";
import { useIntlLocale } from "~/lib/withIntl";
import TranslateTo from "~/src/components/translation/TranslateTo";
import stringOrFirst from "~/src/utils/stringOrFirst";
import VendorPageSeo from "~/components/seo/VendorPageSeo";
import VendorOwnerProfile from "~/components/vendor/profile/owner/VendorOwnerProfile";
import VendorProfile from "~/components/vendor/profile/VendorProfile";

interface IVendorPageProps {
  vendorSlug: string;
  tab: string;
  preview: string | null;
  makers: string | null;
  groupSlug: string | null;
  user: any;
}

export default function VendorPage({ vendorSlug, tab, preview, makers, groupSlug, user }: IVendorPageProps) {
  const router = useRouter();
  const { locale } = useIntlLocale();
  const lang = stringOrFirst(router.query.lang);
  const langExists = typeof lang !== "undefined" && lang.length > 0 && lang !== "en-US";
  const targetLanguage = lang || locale;
  const showTranslateButton = langExists || locale !== "en-US";
  const previewAs = preview ? (makers ? "makers" : "current") : null;
  const queryTab = constructQueryTab(router.query.content, router.query.preview);

  const { data, loading, refetch } = useQuery<IVendorQuery, IVendorQueryVariables>(VendorQuery, {
    variables: {
      slug: vendorSlug,
      language: findLanguageByLocale(lang),
    },
    fetchPolicy: "network-only",
  });

  const translationButton = () => {
    return (
      <TranslateTo
        langExists={langExists}
        showTranslateButton={showTranslateButton}
        refetch={() => {
          refetch({
            slug: vendorSlug,
            language: findLanguageByLocale(targetLanguage),
          });
        }}
        refetchOriginal={() => {
          refetch({
            slug: vendorSlug,
            language: null,
          });
        }}
      />
    );
  };

  if (loading || !data) {
    return (
      <div
        css={{
          padding: 30,
          width: "100%",
          textAlign: "center",
        }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  if (!data.company) {
    return <Redirect to="/" />;
  }

  const { company: vendor } = data;

  if (preview && vendor.viewerIsMember) {
    return (
      <div
        css={{
          paddingTop: 30,
          paddingBottom: 30,
          width: "100%",
        }}
      >
        <VendorPageSeo vendor={vendor} tab={queryTab} />
        <VendorProfile vendor={vendor} tab={tab} preview={previewAs} translationButton={translationButton} />
      </div>
    );
  }

  if (vendor.viewerIsMember) {
    return (
      <>
        <VendorPageSeo vendor={vendor} tab={queryTab} />
        <VendorOwnerProfile vendor={vendor} user={user} tab={tab} groupSlug={groupSlug} />
      </>
    );
  }

  return (
    <>
      <VendorPageSeo vendor={vendor} tab={queryTab} />

      <div
        css={{
          paddingTop: 30,
          paddingBottom: 30,
          width: "100%",
        }}
      >
        <VendorProfile vendor={vendor} tab={tab} preview={null} translationButton={translationButton} />
      </div>
    </>
  );
}

function constructQueryTab(tab: string | string[] | undefined, preview: string | string[] | undefined) {
  if (preview && preview === "1") {
    return "preview";
  }

  const queryTab = Array.isArray(tab) ? tab[0] : tab;
  return queryTab || "";
}
